// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './AdminPrepaidCreditsPage.module.css'
import {
    createPrepaidCodeCheckout,
    listPromoCodes,
    type PromoCodeAdminItem,
} from '../../../services/promoCodes'
import {
    getBillingConfig,
    type BillingConfig,
    type CreditPreset,
} from '../../../services/billing'
import { setCheckoutReturnTo } from '../../../services/checkoutReturn'
import type { HttpError } from '../../../services/http'

const ADMIN_RETURN_TO = '/dashboard?tab=admin'

function formatTimestamp(value?: string | null): string {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleDateString()
}

export function AdminPrepaidCreditsPage(): ReactElement {
    const { t } = useTranslation('dashboard')
    const [codes, setCodes] = useState<PromoCodeAdminItem[]>([])
    const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null)
    const [selectedPreset, setSelectedPreset] = useState<CreditPreset | null>(null)
    const [customCode, setCustomCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isPurchasing, setIsPurchasing] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const getErrorMessage = useCallback(
        (err: unknown, fallbackKey: string): string => {
            const status = (err as HttpError | undefined)?.status
            if (status === 403) return t('prepaidAdminRequired')
            if (status === 401) return t('prepaidSignIn')
            if (status === 400) return t('prepaidInvalidRequest')
            return t(fallbackKey)
        },
        [t]
    )

    const refreshCodes = useCallback(async () => {
        try {
            setIsLoading(true)
            setError(null)
            const response = await listPromoCodes()
            setCodes(response.codes)
        } catch (err) {
            setError(getErrorMessage(err, 'prepaidLoadFailed'))
        } finally {
            setIsLoading(false)
        }
    }, [getErrorMessage])

    useEffect(() => {
        void refreshCodes()
        getBillingConfig()
            .then((config) => {
                setBillingConfig(config)
                setSelectedPreset(config.presets[0] ?? null)
            })
            .catch(() => {})
    }, [refreshCodes])

    const formatAmount = (amount: number): string => {
        if (!billingConfig) return String(amount)
        if (billingConfig.currency === 'krw') {
            return `${billingConfig.symbol}${new Intl.NumberFormat().format(amount)}`
        }
        return `${billingConfig.symbol}${amount}`
    }

    const handlePurchase = useCallback(async () => {
        if (!billingConfig || !selectedPreset || isPurchasing) return
        try {
            setIsPurchasing(true)
            setError(null)
            const codeValue = customCode.trim()
            const res = await createPrepaidCodeCheckout({
                currency: billingConfig.currency,
                amount: selectedPreset.amount,
                code: codeValue.length > 0 ? codeValue : null,
            })
            setCheckoutReturnTo(ADMIN_RETURN_TO)
            window.location.href = res.checkout_url
        } catch (err) {
            setError(getErrorMessage(err, 'prepaidCreateFailed'))
            setIsPurchasing(false)
        }
    }, [billingConfig, selectedPreset, customCode, isPurchasing, getErrorMessage])

    const hasCodes = codes.length > 0
    const now = new Date()

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div>
                    <h2 className={styles.title}>{t('prepaidTitle')}</h2>
                    <p className={styles.subtitle}>{t('prepaidSubtitle')}</p>
                </div>
                <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={refreshCodes}
                    disabled={isLoading}
                >
                    {isLoading ? t('prepaidRefreshing') : t('prepaidRefresh')}
                </button>
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('prepaidCreate')}</div>
                {billingConfig && (
                    <div className={styles.presetGrid}>
                        {billingConfig.presets.map((preset) => (
                            <button
                                key={preset.amount}
                                type="button"
                                className={styles.presetButton}
                                data-selected={selectedPreset?.amount === preset.amount}
                                onClick={() => setSelectedPreset(preset)}
                            >
                                <span className={styles.presetAmount}>
                                    {formatAmount(preset.amount)}
                                </span>
                                <span className={styles.presetCredits}>
                                    {new Intl.NumberFormat().format(preset.credits)}{' '}
                                    {t('common:credits')}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                <div className={styles.formRow}>
                    <label className={styles.label} htmlFor="prepaid-code">
                        {t('prepaidCustomCode')}
                    </label>
                    <input
                        id="prepaid-code"
                        type="text"
                        className={styles.input}
                        placeholder={t('prepaidCustomCodePlaceholder')}
                        value={customCode}
                        onChange={(e) => setCustomCode(e.target.value)}
                    />
                </div>
                <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={() => void handlePurchase()}
                    disabled={!billingConfig || !selectedPreset || isPurchasing}
                >
                    {isPurchasing ? t('prepaidRedirecting') : t('prepaidPurchase')}
                </button>
                <p className={styles.paymentNote}>{t('prepaidPaymentNote')}</p>
                {error && <div className={styles.error}>{error}</div>}
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('prepaidActiveCodes')}</div>
                {!hasCodes && !isLoading && (
                    <div className={styles.emptyState}>{t('prepaidNoCodes')}</div>
                )}
                {hasCodes && (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('prepaidCode')}</th>
                                    <th>{t('prepaidCredits')}</th>
                                    <th>{t('prepaidExpires')}</th>
                                    <th>{t('prepaidStatus')}</th>
                                    <th>{t('prepaidRedeemedBy')}</th>
                                    <th>{t('prepaidRedeemedAt')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {codes.map((code) => {
                                    const expiresAt = new Date(code.valid_until)
                                    const isExpired =
                                        Number.isNaN(expiresAt.getTime()) ||
                                        expiresAt < now
                                    const isUsed =
                                        Boolean(code.redeemed_at) ||
                                        code.used_count > 0
                                    const status = isExpired
                                        ? t('prepaidStatusExpired')
                                        : isUsed
                                            ? t('prepaidStatusUsed')
                                            : t('prepaidStatusUnused')
                                    return (
                                        <tr key={code.id}>
                                            <td className={styles.mono}>{code.code}</td>
                                            <td>{new Intl.NumberFormat().format(code.credits)}</td>
                                            <td>{formatTimestamp(code.valid_until)}</td>
                                            <td>{status}</td>
                                            <td className={styles.mono}>
                                                {code.redeemed_by_email || '—'}
                                            </td>
                                            <td>{formatTimestamp(code.redeemed_at)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
